/**
 * The single source of route templates for analytics.
 *
 * Templates, never concrete paths: /shared/:token is a live route and its raw
 * value is a share secret, so a raw-path capture would put a secret in the
 * analytics store (PRIVACY_AND_RETENTION.md).
 *
 * Keep in sync with App.tsx AND with backend/app/services/page_events.py
 * (ROUTE_TEMPLATES there is the server-side allowlist — the two lists are
 * kept in sync by hand; an unlisted route is silently unmeasured until it
 * is added to both). A path that matches nothing is reported as (404)
 * rather than guessed at — an invented template is worse than a known
 * unknown.
 */
import { matchPath } from "react-router-dom";

export const UNKNOWN_ROUTE = "(404)";

export const ROUTE_TEMPLATES: readonly string[] = [
  "/",
  "/pricing",
  "/privacy",
  "/terms",
  "/refunds",
  "/contact",
  "/questions",
  "/login",
  "/forgot-password",
  "/reset-password",
  "/early-career",
  "/early-career/teardown",
  "/shared/:token",
  "/agentic-ai-mastery",
  "/agentic-ai-mastery/module/:slug",
  "/agentic-ai-mastery/project/:id",
  "/agentic-ops",
  "/agentic-ops/module/:slug",
  "/agentic-interviewing",
  "/agentic-interviewing/level/:levelNumber",
  "/agentic-interviewing/scenario/:slug",
  "/career-transition",
  "/career-transition/framework/:slug",
  "/claude-harness",
  "/claude-harness/module/:slug",
  "/claude-harness/scorecard",
  "/coach",
  "/linkedin-presence",
  "/linkedin-presence/content",
  "/linkedin-presence/outreach",
  "/linkedin-presence/profile",
  "/portfolio-kit",
  "/portfolio-kit/archetype/:slug",
  "/portfolio-kit/demo",
  "/portfolio-kit/deployment-checklist",
  "/portfolio-kit/lovable-prompt",
  "/portfolio-kit/modules/:slug",
  "/portfolio-kit/readme-template",
  "/resume-mastery",
  "/resume-mastery/ats-score",
  "/resume-mastery/demos",
  "/resume-mastery/demos/:slug",
  "/resume-mastery/principles",
  "/resume-mastery/progress",
  "/resume-mastery/prompts",
  "/resume-mastery/prompts/:slug",
  "/visibility-strategy",

  // Authenticated workspace routes — omitted from the original allowlist
  // (the plan's route-list was built from a single-line Route-path
  // regex that missed these multi-line JSX declarations). Fixed round 1:
  // these are the product's most-engaged traffic, so leaving them out of
  // the allowlist meant they resolved to (404) and inflated the dead-end
  // rate (M-03) with healthy usage. See routeTemplates.test.ts, which
  // asserts every live App.tsx route is a member of this list.
  "/admin/metrics",
  "/admin/prompts",
  "/analytics",
  "/answers/:answerId/compare",
  "/billing/success",
  "/career-transition/assessment",
  "/career-transition/roadmap",
  "/career-transition/roadmap-history",
  "/career-transition/roadmap/:id",
  "/coaching",
  "/design-lab",
  "/design-lab/primitives",
  "/evaluate",
  "/evaluations/:id",
  "/forge-coach",
  "/forge-coach/about",
  "/generator",
  "/learn/agentic-mastery/:slug/exercises/:exerciseId",
  "/mock",
  "/moderation",
  "/resume-mastery/workspace",
  "/resume-mastery/workspace/targets/:id",
  "/templates",
];

/** Longest-first, so "/resume-mastery/demos/:slug" wins over "/resume-mastery". */
const ORDERED = [...ROUTE_TEMPLATES].sort(
  (a, b) => b.split("/").length - a.split("/").length || b.length - a.length,
);

export function resolveRouteTemplate(pathname: string): string | null {
  for (const t of ORDERED) {
    if (matchPath({ path: t, end: true }, pathname)) return t;
  }
  return null;
}
